import dayjs from 'dayjs';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import { DocumentsFilters } from '@totem/components/documents/filters';
import { DocumentReference } from '@totem/components/documents/types';
import { Filter } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';

export const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const addDocumentFilters = (
  currentFilters: Filter[],
  staticFilters: DocumentsFilters,
  documentReference?: DocumentReference,
) => {
  let referenceSatisfied = false;
  if (isNotNull(documentReference)) {
    if (
      isNotNull(documentReference.objectReference) &&
      documentReference.objectReference.id !== '' &&
      documentReference.objectReference.id !== EMPTY_ID
    ) {
      currentFilters.push({
        field: 'referencedItems.id',
        values: [documentReference.objectReference.id],
      });

      referenceSatisfied = true;
    }
    if (
      isNotNull(documentReference.objectReference) &&
      documentReference.objectReference.type !== ''
    ) {
      currentFilters.push({
        field: 'referencedItems.type',
        values: [documentReference.objectReference.type],
      });
    }
    if (
      !referenceSatisfied &&
      isNotNull(documentReference.controlSystemId) &&
      documentReference.controlSystemId !== EMPTY_ID
    ) {
      currentFilters.push({
        field: 'controlSystemId',
        values: [documentReference.controlSystemId],
      });

      referenceSatisfied = true;
    }
    if (
      !referenceSatisfied &&
      isNotNull(documentReference.buildingId) &&
      documentReference.buildingId !== EMPTY_ID
    ) {
      currentFilters.push({
        field: 'buildingId',
        values: [documentReference.buildingId],
      });

      referenceSatisfied = true;
    }
    if (
      !referenceSatisfied &&
      isNotNull(documentReference.regionId) &&
      documentReference.regionId !== EMPTY_ID
    ) {
      currentFilters.push({
        field: 'regionId',
        values: [documentReference.regionId],
      });

      referenceSatisfied = true;
    }
  }

  if (isNotNull(staticFilters)) {
    if (isNotNull(staticFilters.name) && staticFilters.name !== '') {
      currentFilters.push({
        field: 'displayName',
        operator: 'cscontains',
        values: [staticFilters.name],
      });
    }

    if (isNotNull(staticFilters.tag) && staticFilters.tag !== '') {
      currentFilters.push({
        field: 'tags',
        operator: 'cscontains',
        values: [staticFilters.tag],
      });
    }

    if (
      isNotNull(staticFilters.regionIds) &&
      staticFilters.regionIds.length > 0
    ) {
      currentFilters.push({
        field: 'regionId',
        values: staticFilters.regionIds,
      });
    }

    if (
      isNotNull(staticFilters.buildingIds) &&
      staticFilters.buildingIds.length > 0
    ) {
      currentFilters.push({
        field: 'buildingId',
        values: staticFilters.buildingIds,
      });
    }

    if (
      isNotNull(staticFilters.controlSystemIds) &&
      staticFilters.controlSystemIds.length > 0
    ) {
      currentFilters.push({
        field: 'controlSystemId',
        values: staticFilters.controlSystemIds,
      });
    }

    if (
      isNotNull(staticFilters.objectTypes) &&
      staticFilters.objectTypes.length > 0
    ) {
      currentFilters.push({
        field: 'objectType',
        values: staticFilters.objectTypes,
      });
    }

    if (
      isNotNull(staticFilters.objectIds) &&
      staticFilters.objectIds.length > 0
    ) {
      currentFilters.push({
        field: 'objectId',
        values: staticFilters.objectIds,
      });
    }

    if (
      isNotNull(staticFilters.referencedItemType) &&
      staticFilters.referencedItemType !== ''
    ) {
      currentFilters.push({
        field: 'referencedItems.type',
        values: [staticFilters.referencedItemType],
      });
    }

    if (
      isNotNull(staticFilters.referencedItemNames) &&
      staticFilters.referencedItemNames.length > 0
    ) {
      currentFilters.push({
        field: 'referencedItems.name',
        values: staticFilters.referencedItemNames,
      });
    }
  }

  return currentFilters;
};

// Documents added by contract-sync-service (Sales Order statements of work).
export const CONTRACT_SYNC_USER = 'contract-sync-service';

export const creatorLabel = (createdBy: string) => {
  if (createdBy === CONTRACT_SYNC_USER) {
    return 'Contract sync';
  }
  return createdBy || 'Not recorded';
};

export const fileTypeLabel = (fileType: string) =>
  fileType.toLowerCase() === 'link'
    ? 'Web link'
    : (fileType || 'Unknown').toUpperCase();

export const formatFileSize = (bytes: number) =>
  bytes >= 1048576
    ? `${(bytes / 1048576).toFixed(1)} MB`
    : `${Math.round(bytes / 1000)} KB`;

// Matches the server's expiryState filter and the summary's 90-day window.
export const EXPIRY_STATES = ['Expired', 'Within 90 days', 'Later'];

export const daysUntilExpiry = (expirationDate: string) =>
  dayjs(expirationDate).diff(dayjs(), 'day');

export const expiryState = (expirationDate: string) => {
  const days = daysUntilExpiry(expirationDate);
  if (days < 0) {
    return 'Expired';
  }
  return days < 90 ? 'Within 90 days' : 'Later';
};

// The reference fields the upload and web-link endpoints expect; empty ids mean organization-level.
export const referenceFields = (reference?: DocumentReference | null) => ({
  regionId: reference?.regionId || EMPTY_ID,
  buildingId: reference?.buildingId || EMPTY_ID,
  controlSystemId: reference?.controlSystemId || EMPTY_ID,
  objectType: reference?.objectReference?.type ?? '',
  objectId: reference?.objectReference?.id ?? '',
  objectDisplay: reference?.objectReference?.name ?? '',
});
